// SignalWire browser softphone via SIP.js — mimics the Twilio Voice SDK
// surface the Dialer used (Device.connect / call.on / call.disconnect /
// call.mute / call.sendDigits) so the rest of Dialer.jsx doesn't have to
// change.
//
// WHY SIP.js instead of @signalwire/js (Fabric)?
// -----------------------------------------------
// The @signalwire/js v3+ Fabric SDK CANNOT join LaML `<Conference>`
// rooms — it only reaches Fabric rooms. Our backend power dialer uses
// TwiML `<Dial><Conference>` (LaML) because we need PSTN AMD, recording,
// and the standard telephony conference flow. So the ONLY documented
// way (per SignalWire Feb 2026 docs) for a browser client to reach that
// LaML conference is:
//   1. Register as a SIP endpoint on SignalWire via SIP-over-WSS
//   2. Send a SIP INVITE with an `X-Conference-Name` header
//   3. SignalWire's SIP endpoint call_handler webhook returns TwiML
//      `<Dial><Conference>{same_name}</Conference></Dial>` — bridging
//      the browser leg into the same conference the customer is in.
//
// This file's public surface (`Device`, `createDeviceAdapter`) is
// unchanged from the old Fabric shim so Dialer.jsx keeps working.
import {
  UserAgent, Registerer, RegistererState, Inviter, SessionState,
} from "sip.js";
import { api } from "@/lib/api";

// One persistent hidden <audio autoplay> tag per tab for remote RTP
// playback. WITHOUT this element attached to a MediaStream, the browser
// plays no audio (ringback / callee voice) even if the SIP session is
// technically established.
function _remoteAudioEl() {
  if (typeof document === "undefined") return null;
  let el = document.getElementById("sw-sip-audio");
  if (!el) {
    el = document.createElement("audio");
    el.id = "sw-sip-audio";
    el.autoplay = true;
    el.playsInline = true;
    // Hide but keep mounted — display:none breaks playback in some
    // browsers, so position off-screen instead.
    el.style.cssText = "position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;";
    document.body.appendChild(el);
  }
  return el;
}

// SIP UserAgent lifecycle — one per tab, cached.
let _uaPromise = null;
let _uaCreds   = null;

async function _fetchCreds() {
  if (_uaCreds) return _uaCreds;
  const { data } = await api.get("/voice/sip-credentials");
  if (!data?.username || !data?.password || !data?.wss || !data?.domain) {
    throw new Error("Missing SIP credentials from backend");
  }
  _uaCreds = data;
  return data;
}

async function getUA() {
  if (_uaPromise) return _uaPromise;
  _uaPromise = (async () => {
    const creds = await _fetchCreds();
    const uri = UserAgent.makeURI(`sip:${creds.username}@${creds.domain}`);
    if (!uri) throw new Error(`Invalid SIP URI (username=${creds.username} domain=${creds.domain})`);
    const ua = new UserAgent({
      uri,
      authorizationUsername: creds.username,
      authorizationPassword: creds.password,
      transportOptions: { server: creds.wss },
      logBuiltinEnabled: false,
      sessionDescriptionHandlerFactoryOptions: {
        constraints: { audio: true, video: false },
        peerConnectionConfiguration: {
          // Prefer TURN + STUN minted server-side from SignalWire's
          // Network Traversal Service (1-hour ephemeral creds — see
          // /api/voice/sip-credentials). Without TURN, reps behind
          // symmetric NAT / corporate firewalls can't negotiate media
          // → customer answers into silence → TCPA abandonment risk.
          // If the backend didn't return an ice_servers list (e.g. old
          // deployment, mint failed), fall back to public STUN so the
          // softphone still works on friendly networks.
          iceServers: (creds.ice_servers && creds.ice_servers.length > 0)
            ? creds.ice_servers
            : [{ urls: ["stun:stun.l.google.com:19302"] }],
          iceTransportPolicy: "all",
        },
      },
    });
    console.log("[SIP] Using iceServers:", creds.ice_servers?.length || 0, "entries", creds.ice_servers);
    // Log transport failures — critical for debugging WSS handshake
    // issues that used to fail silently. Any failure here is why reps
    // hear no ringback + see no popup: without a WSS transport the
    // browser never sends a SIP INVITE.
    try {
      ua.transport.onConnect = () => console.log("[SIP] WSS connected", creds.wss);
      ua.transport.onDisconnect = (e) => console.warn("[SIP] WSS disconnected", e);
    } catch (_) { /* older sip.js versions */ }
    try {
      await ua.start();
    } catch (e) {
      throw new Error(`SIP WSS handshake failed → ${creds.wss}: ${e?.message || e}`);
    }
    const registerer = new Registerer(ua, {
      // Long expiry (1h) + refresh at 50% (30min) so the browser leg
      // never dwells in an Unregistered state between calls. SignalWire's
      // default REGISTER expiry can be short (~60s on some endpoints),
      // and if it lapses we'd get 403 on the very next INVITE — that was
      // the root cause of "Browser leg dropped before audio connected"
      // when reps waited a minute between calls. Overriding the expires
      // header forces our long-lived window.
      expires: 3600,
      refreshFrequency: 50,
    });
    // ─── sip.js safety patch ─────────────────────────────────────────
    // Some SIP registrars (SignalWire included) can return REGISTER
    // 200-OK responses with unexpected/missing Contact `expires=`. sip.js
    // would then schedule the "expired" timer with NaN*1000=0ms and flip
    // straight to Unregistered — nuking auth for the next INVITE. Guard
    // by coercing NaN/non-positive server-supplied expires to our
    // requested 3600s window BEFORE the timers are scheduled.
    const _origRegistered = registerer.registered.bind(registerer);
    registerer.registered = (expiresFromServer) => {
      const safe = (Number.isFinite(expiresFromServer) && expiresFromServer > 0)
        ? expiresFromServer
        : 3600;
      if (safe !== expiresFromServer) {
        console.log("[SIP] Coerced bad server-supplied expires (" + expiresFromServer + ") → " + safe + "s");
      }
      return _origRegistered(safe);
    };
    registerer.stateChange.addListener((s) => {
      console.log("[SIP] Registerer state:", s);
      // Auto-heal: if we DO end up Unregistered (rare, only if the
      // server truly refused refresh), re-register once so the NEXT
      // INVITE authenticates cleanly.
      if (s === RegistererState.Unregistered) {
        setTimeout(() => {
          try {
            if (registerer.state === RegistererState.Unregistered) {
              console.log("[SIP] Auto re-registering…");
              registerer.register().catch((e) => {
                console.warn("[SIP] Auto re-register failed:", e?.message || e);
              });
            }
          } catch (_) { /* ignore */ }
        }, 300);
      }
    });
    try {
      // Pass a delegate so we can distinguish "server rejected our
      // REGISTER" from "sip.js internal parse quirk". If onReject fires
      // with 401/403, the endpoint credentials in .env don't match
      // SignalWire's SIP registrar — see /api/voice/sip-credentials.
      await registerer.register({
        requestDelegate: {
          onAccept: (response) => {
            const sc = response?.message?.statusCode;
            console.log("[SIP] REGISTER onAccept — status:", sc);
          },
          onReject: (response) => {
            const sc = response?.message?.statusCode;
            console.warn("[SIP] REGISTER onReject — status:", sc,
              "(check SIGNALWIRE_SIP_USERNAME/PASSWORD in backend/.env vs the SIP endpoint on SignalWire dashboard)");
          },
        },
      });
    } catch (e) {
      throw new Error(`SIP REGISTER rejected (${creds.username}@${creds.domain}): ${e?.message || e}`);
    }
    console.log("[SIP] Registered as", `${creds.username}@${creds.domain}`);
    return { ua, registerer, creds };
  })().catch((e) => {
    _uaPromise = null; _uaCreds = null;
    // Re-throw so callers can toast it.
    console.error("[SIP] Registration failed:", e?.message || e);
    throw e;
  });
  return _uaPromise;
}

export async function destroySWClient() {
  if (!_uaPromise) return;
  const p = _uaPromise;
  _uaPromise = null; _uaCreds = null;
  try {
    const { ua, registerer } = await p;
    try { await registerer.unregister(); } catch (_) { /* ignore */ }
    try { await ua.stop(); } catch (_) { /* ignore */ }
  } catch (_) { /* ignore */ }
}

// Attach every remote audio track from the session's peer connection to
// our <audio> element. Called on session-established.
function _wireMedia(session) {
  const audio = _remoteAudioEl();
  if (!audio) return;
  const pc = session.sessionDescriptionHandler?.peerConnection;
  if (!pc) return;
  const stream = new MediaStream();
  pc.getReceivers().forEach((r) => { if (r.track) stream.addTrack(r.track); });
  audio.srcObject = stream;
  audio.play().catch(() => { /* browser will retry on next user gesture */ });
}

// `new Device(token, opts)` constructor — Twilio-compatible signature.
export class Device {
  constructor(_token, _opts) {
    return createDeviceAdapter();  // eslint-disable-line no-constructor-return
  }
}

// Returns an object mimicking Twilio's Device with the small subset of
// methods the Dialer actually uses.
export function createDeviceAdapter() {
  const listeners = new Map();
  const emit = (name, ...args) => {
    const set = listeners.get(name);
    if (!set) return;
    for (const fn of set) { try { fn(...args); } catch (_) { /* swallow */ } }
  };
  const on = (name, fn) => {
    if (!listeners.has(name)) listeners.set(name, new Set());
    listeners.get(name).add(fn);
    return () => listeners.get(name)?.delete(fn);
  };

  let currentSession = null;
  let currentCallSid = null;

  const wrapCall = (session) => {
    const callListeners = new Map();
    const cEmit = (name, ...args) => {
      const set = callListeners.get(name);
      if (!set) return;
      for (const fn of set) { try { fn(...args); } catch (_) { /* ignore */ } }
    };
    const cOn = (name, fn) => {
      if (!callListeners.has(name)) callListeners.set(name, new Set());
      callListeners.get(name).add(fn);
      return () => callListeners.get(name)?.delete(fn);
    };

    // Map SIP.js session state → Twilio-style events the Dialer expects.
    session.stateChange.addListener((state) => {
      console.log("[SIP] Session state:", state);
      if (state === SessionState.Establishing) cEmit("ringing");
      if (state === SessionState.Established) {
        _wireMedia(session);
        try {
          const pc = session.sessionDescriptionHandler?.peerConnection;
          if (pc) {
            pc.addEventListener("iceconnectionstatechange", () => {
              console.log("[WebRTC] iceConnectionState:", pc.iceConnectionState);
              if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
                console.log("[AUDIO] two-way audio negotiated (iceConnectionState=" + pc.iceConnectionState + ")");
              }
            });
            pc.addEventListener("connectionstatechange", () => {
              console.log("[WebRTC] connectionState:", pc.connectionState);
            });
          }
        } catch (_) { /* ignore */ }
        cEmit("accept", { parameters: { CallSid: currentCallSid || session.id } });
      }
      if (state === SessionState.Terminated || state === SessionState.Terminating) {
        cEmit("disconnect");
      }
    });

    return {
      parameters: { CallSid: session.id },
      on: cOn,
      async disconnect() {
        try {
          if (session.state === SessionState.Established) await session.bye();
          else if (session.cancel) await session.cancel();
        } catch (_) { /* ignore */ }
      },
      async mute(shouldMute) {
        const pc = session.sessionDescriptionHandler?.peerConnection;
        if (!pc) return;
        pc.getSenders().forEach((sender) => {
          if (sender.track && sender.track.kind === "audio") {
            sender.track.enabled = !shouldMute;
          }
        });
        cEmit("mute", !!shouldMute);
      },
      async sendDigits(digits) {
        try {
          // SIP.js exposes DTMF via session.info() with proper body, but
          // the modern path is through the SessionDescriptionHandler.
          const sdh = session.sessionDescriptionHandler;
          if (sdh?.sendDtmf) {
            for (const d of String(digits)) { await sdh.sendDtmf(d); }
          }
        } catch (e) {
          cEmit("error", { code: 0, message: e?.message || "dtmf failed" });
        }
      },
    };
  };

  return {
    async register() {
      try {
        await getUA();
        emit("registered");
      } catch (e) {
        emit("error", { code: 0, message: e?.message || "SIP register failed" });
        throw e;
      }
    },
    on,
    async destroy() {
      try { currentSession && (await (currentSession.bye?.() ?? currentSession.cancel?.())); }
      catch (_) { /* ignore */ }
      currentSession = null;
      await destroySWClient();
      emit("unregistered");
    },

    // The Dialer calls Device.connect({ params: { conf } }) after it has
    // obtained a conference name from POST /api/dialer/prepare. The SIP
    // INVITE targets the registered SIP endpoint URI with NO custom
    // headers — SignalWire strips them for browser-endpoint-originated
    // calls (proven 2026-08-18). The LaML webhook now looks up the
    // pending call intent by SIP username (server-side) and returns
    // `<Dial><Conference>{conf}</Conference></Dial>` accordingly.
    async connect({ params }) {
      const conf  = params?.conf;
      if (!conf) {
        throw new Error("Dial requires a conference name (conf) — call /api/dialer/prepare first");
      }
      // Ensure mic permission BEFORE placing the INVITE. Without this,
      // some Chromium builds emit an INVITE with no audio m-line.
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        emit("error", { code: 0, message: "Microphone permission denied" });
        throw e;
      }
      const { ua, creds, registerer } = await getUA();
      // Guard: if the Registerer has slipped to Unregistered since the
      // last call (expiry, transient WSS blip, refresh 4xx), re-register
      // BEFORE sending the INVITE.
      if (registerer && registerer.state !== RegistererState.Registered) {
        console.log("[SIP] Registerer not Registered (state=" + registerer.state + ") — re-registering before INVITE");
        try { await registerer.register(); }
        catch (e) { console.warn("[SIP] Pre-invite register failed:", e?.message || e); }
      }
      // Plain endpoint URI, no headers — the LaML webhook resolves the
      // conference from the server-side call intent.
      const target = UserAgent.makeURI(`sip:${creds.username}@${creds.domain}`);
      if (!target) throw new Error(`Invalid SIP URI (${creds.username}@${creds.domain})`);

      const inviter = new Inviter(ua, target, {
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
      currentSession = inviter;
      currentCallSid = `sip-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      console.log("[SIP] INVITE destination:", `sip:${creds.username}@${creds.domain}`, "conf:", conf);
      const wrapper = wrapCall(inviter);
      await inviter.invite({
        requestDelegate: {
          onTrying:  (r) => console.log("[SIP] 100 Trying"),
          onProgress:(r) => console.log("[SIP]", r?.message?.statusCode, r?.message?.reasonPhrase || ""),
          onAccept:  (r) => console.log("[SIP] 200 OK"),
          onRedirect:(r) => console.log("[SIP]", r?.message?.statusCode, "Redirect"),
          onReject:  (r) => console.warn("[SIP]", r?.message?.statusCode, r?.message?.reasonPhrase || "Rejected"),
        },
      });
      // Hook ICE diagnostics as early as possible so the console captures
      // gathering + connection state before the browser leg ever reaches
      // Established. Diagnoses TURN allocation failures / SDP mismatches.
      try {
        const pc = inviter.sessionDescriptionHandler?.peerConnection;
        if (pc) {
          pc.addEventListener("iceconnectionstatechange", () => {
            console.log("[WebRTC] iceConnectionState:", pc.iceConnectionState);
          });
          pc.addEventListener("connectionstatechange", () => {
            console.log("[WebRTC] connectionState:", pc.connectionState);
          });
        }
      } catch (_) { /* ignore */ }
      return wrapper;
    },
  };
}
